import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlinx.browser.window
import kotlinx.coroutines.launch

// Fáze 5 — CMS admin (jen role admin): úvodní text na Home (sg_content) + aktuality (sg_news).
// Text se ukládá jako prostý text; na Home se renderuje bezpečně (bez HTML injektáže).

@Composable
fun ContentAdminSection(token: String) {
    var intro by remember { mutableStateOf("") }
    var news by remember { mutableStateOf<List<SgNewsDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var reloadKey by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(reloadKey) {
        try {
            intro = Api.fetchContent("home_intro") ?: ""
            news = Api.fetchNews(token)
        } catch (e: Throwable) {
            error = e.apiMessage()
        } finally {
            loading = false
        }
    }

    if (loading) {
        Div(attrs = { classes("card") }) { P { Text("Načítám…") } }
        return
    }
    error?.let { Div(attrs = { classes("card") }) { P { Text("Chyba: $it") } } }

    IntroEditor(token, intro, onSaved = { intro = it })
    NewsEditor(token, news, onChanged = { reloadKey++ })
}

@Composable
private fun IntroEditor(token: String, initial: String, onSaved: (String) -> Unit) {
    var text by remember(initial) { mutableStateOf(initial) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    Div(attrs = { classes("card") }) {
        H3 { Text("Úvodní text (Home)") }
        P(attrs = { classes("muted") }) { Text("Zobrazuje se v sekci „O týmu“ na hlavní stránce.") }
        ContentEditor(initial, onHtmlChange = { text = it })
        Div(attrs = { classes("form-actions") }) {
            Button(attrs = {
                classes("primary-btn")
                if (busy) disabled()
                onClick {
                    busy = true; message = null
                    scope.launch {
                        try {
                            Api.saveContent(token, "home_intro", text)
                            message = "Uloženo."
                            onSaved(text)
                        } catch (e: Throwable) {
                            message = "Uložení selhalo: ${e.apiMessage()}"
                        } finally { busy = false }
                    }
                }
            }) { Text(if (busy) "Ukládám…" else "Uložit text") }
            message?.let { Span(attrs = { classes("form-note") }) { Text(it) } }
        }
    }
}

@Composable
private fun NewsEditor(token: String, news: List<SgNewsDto>, onChanged: () -> Unit) {
    var editingId by remember { mutableStateOf<String?>(null) }

    Div(attrs = { classes("card") }) {
        Div(attrs = { classes("training-item-top") }) {
            H3 { Text("Aktuality") }
            if (editingId != "new") {
                Button(attrs = { classes("small-btn"); onClick { editingId = "new" } }) { Text("+ přidat aktualitu") }
            }
        }

        if (editingId == "new") {
            key("new") {
                NewsForm(token, null, onDone = { editingId = null; onChanged() }, onCancel = { editingId = null })
            }
        }

        if (news.isEmpty() && editingId != "new") {
            P(attrs = { classes("muted") }) { Text("Zatím žádné aktuality.") }
            return@Div
        }

        Div(attrs = { classes("news-admin-list") }) {
            news.forEach { n ->
                if (editingId == n.id) {
                    key(n.id) {
                        NewsForm(token, n, onDone = { editingId = null; onChanged() }, onCancel = { editingId = null })
                    }
                } else {
                    NewsAdminRow(token, n, onEdit = { editingId = n.id }, onChanged = onChanged)
                }
            }
        }
    }
}

@Composable
private fun NewsAdminRow(token: String, n: SgNewsDto, onEdit: () -> Unit, onChanged: () -> Unit) {
    val scope = rememberCoroutineScope()
    Div(attrs = { classes("news-admin-item") }) {
        Div(attrs = { classes("news-admin-head") }) {
            Span(attrs = { classes("news-title") }) { Text(n.title) }
            if (!n.published) Span(attrs = { classes("badge") }) { Text("skryté") }
            n.createdAt?.take(10)?.let { Span(attrs = { classes("news-date") }) { Text(it) } }
        }
        if (n.body.isNotBlank()) Div(attrs = { classes("news-body") }) { RichContent(n.body) }
        Div(attrs = { classes("training-actions") }) {
            Button(attrs = { classes("small-btn"); onClick { onEdit() } }) { Text("Upravit") }
            Button(attrs = {
                classes("small-btn", "danger-btn")
                onClick {
                    if (window.confirm("Smazat aktualitu „${n.title}“?")) {
                        scope.launch {
                            try { Api.deleteNews(token, n.id); onChanged() } catch (_: Throwable) {}
                        }
                    }
                }
            }) { Text("Smazat") }
        }
    }
}

@Composable
private fun NewsForm(token: String, editing: SgNewsDto?, onDone: () -> Unit, onCancel: () -> Unit) {
    var title by remember { mutableStateOf(editing?.title ?: "") }
    var body by remember { mutableStateOf(editing?.body ?: "") }
    var published by remember { mutableStateOf(editing?.published ?: true) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    Div(attrs = { classes("news-form") }) {
        Input(type = InputType.Text) {
            classes("field"); placeholder("nadpis"); value(title); onInput { title = it.value }
        }
        Div(attrs = { classes("editor-label") }) { Text("Text aktuality") }
        ContentEditor(editing?.body ?: "", onHtmlChange = { body = it })
        Label(attrs = { classes("cms-check") }) {
            CheckboxInput(checked = published) { onChange { published = it.value } }
            Text(" Publikovat (viditelné na webu)")
        }
        Div(attrs = { classes("form-actions") }) {
            Button(attrs = {
                classes("primary-btn")
                if (busy) disabled()
                onClick {
                    if (title.isBlank()) { message = "Zadej nadpis."; return@onClick }
                    busy = true; message = null
                    scope.launch {
                        try {
                            if (editing == null) Api.createNews(token, title.trim(), body, published)
                            else Api.updateNews(token, editing.id, title.trim(), body, published)
                            onDone()
                        } catch (e: Throwable) {
                            message = "Uložení selhalo: ${e.apiMessage()}"
                        } finally { busy = false }
                    }
                }
            }) { Text(if (busy) "Ukládám…" else if (editing == null) "Přidat" else "Uložit") }
            Button(attrs = { classes("small-btn"); onClick { onCancel() } }) { Text("Zrušit") }
            message?.let { Span(attrs = { classes("form-note") }) { Text(it) } }
        }
    }
}
